import { getIdToken, useAuth } from '@pooflabs/web';
import { ArrowLeft, Check, Clock, Copy, Home, Info, Loader2, Paperclip, Send, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { AgentAvatar } from '@/components/AgentAvatar';
import { useActiveAgentRental } from '@/hooks/use-active-agent-rental';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { createAuthenticatedApiClient } from '@/lib/api-client';
import { subscribeAgents, type AgentsResponse } from '@/lib/collections/agents';
import { setChatMessages, subscribeManyChatMessages, type ChatMessagesResponse } from '@/lib/collections/chatMessages';
import { Address, Time } from '@/lib/db-client';
import { getAgentHelperText, isFunctionalAgentId } from '@/utils/agentContent';
import { formatDuration } from '@/utils/format';
import { getOrCreateSessionId, payForCall } from '@/utils/hireAgent';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function fileToText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) ?? '');
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

type AttachmentKind = 'audio' | 'image' | 'document';

interface PendingAttachmentPayload {
  kind: AttachmentKind;
  mimeType: string;
  data: string;
  filename: string;
}

const ACCEPTED_AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/mp4', 'audio/m4a', 'audio/x-m4a'];
const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const ACCEPTED_DOCUMENT_TYPES = ['text/plain', 'text/markdown'];

// Kept in sync with the size caps in partyserver/src/utils/agentAi.ts —
// audio is sent as raw bytes (base64-encoded for the JSON request) straight
// to Deepgram, images ride along as a base64 data URL, and documents are
// inlined as plain text directly into the agent's prompt.
const MAX_AUDIO_BYTES = 10_000_000;
const MAX_IMAGE_BYTES = 4_000_000;
const MAX_DOCUMENT_BYTES = 200_000;

function detectAttachmentKind(file: File): AttachmentKind | null {
  if (ACCEPTED_AUDIO_TYPES.includes(file.type) || /\.(mp3|wav|m4a)$/i.test(file.name)) return 'audio';
  if (ACCEPTED_IMAGE_TYPES.includes(file.type) || /\.(png|jpe?g|gif|webp)$/i.test(file.name)) return 'image';
  if (ACCEPTED_DOCUMENT_TYPES.includes(file.type) || /\.(txt|md)$/i.test(file.name)) return 'document';
  return null;
}

function maxBytesFor(kind: AttachmentKind): number {
  if (kind === 'audio') return MAX_AUDIO_BYTES;
  if (kind === 'image') return MAX_IMAGE_BYTES;
  return MAX_DOCUMENT_BYTES;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = React.useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <button
      onClick={copy}
      className="text-xs px-2 py-1 rounded bg-zinc-700 hover:bg-zinc-600 transition"
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

// Emitted by the backend (agentAi.ts runAgentTurn) when the imageGeneration
// or videoGeneration capability fires — deapi.ai generation is async, so the
// chat reply arrives with just a request_id to poll instead of a final URL.
// (Not standard markdown, so it's stripped out and rendered as its own
// polling widget before the rest of the text is handed to ReactMarkdown.)
const GENERATING_MARKER_REGEX = /\[\[GENERATING:(image|video):([a-zA-Z0-9_-]+)\]\]/g;

interface GenerationStatusResponse {
  status: 'pending' | 'processing' | 'done' | 'error';
  progress: number;
  resultUrl: string | null;
  error: string | null;
}

/**
 * Polls /api/generation-status/:requestId every ~2s until the deapi.ai job
 * is done (renders the image/video inline) or errors (shows the real reason
 * returned by deapi.ai). Used for both the imageGeneration and
 * videoGeneration capabilities' in-chat tool-call results.
 */
const GeneratingMediaBubble: React.FC<{ kind: 'image' | 'video'; requestId: string }> = ({ kind, requestId }) => {
  const { user } = useAuth();
  const [state, setState] = useState<{
    status: GenerationStatusResponse['status'];
    progress: number;
    resultUrl: string | null;
    error: string | null;
  }>({ status: 'pending', progress: 0, resultUrl: null, error: null });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      if (cancelled) return;
      if (!user?.address) {
        timer = setTimeout(poll, 2000);
        return;
      }
      try {
        const token = await getIdToken();
        if (!token) {
          timer = setTimeout(poll, 2000);
          return;
        }
        const authApi = createAuthenticatedApiClient(token, user.address);
        const result = await authApi.get<GenerationStatusResponse>(`/api/generation-status/${requestId}`);
        if (cancelled) return;
        setState({
          status: result.status,
          progress: typeof result.progress === 'number' ? result.progress : 0,
          resultUrl: result.resultUrl ?? null,
          error: result.error ?? null,
        });
        if (result.status !== 'done' && result.status !== 'error') {
          timer = setTimeout(poll, 2000);
        }
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          status: 'error',
          error: err instanceof Error && err.message ? err.message : 'Could not check generation status.',
        }));
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [requestId, user?.address]);

  if (state.status === 'error') {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-xs px-3 py-2 my-2">
        {state.error || `${kind === 'image' ? 'Image' : 'Video'} generation failed.`}
      </div>
    );
  }

  if (state.status === 'done' && state.resultUrl) {
    return kind === 'image' ? (
      <img src={state.resultUrl} alt="Generated" className="max-w-full rounded-xl border border-border my-2" />
    ) : (
      <video src={state.resultUrl} controls className="max-w-full rounded-xl border border-border my-2" />
    );
  }

  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 my-2 text-xs text-muted-foreground">
      <Loader2 className="w-3.5 h-3.5 animate-spin" />
      <span>
        {kind === 'image' ? 'Generating image…' : 'Generating video…'}
        {state.progress ? ` ${Math.round(state.progress)}%` : ''}
      </span>
    </div>
  );
};

/**
 * Full markdown rendering for agent/assistant replies via react-markdown +
 * remark-gfm (headers, bold/italic, lists, links, tables, strikethrough,
 * task lists, blockquotes, inline `code`). Fenced ```lang ... ``` blocks get
 * a custom renderer with a language label + copy-button header, monospace,
 * preserved whitespace. Styled to match the chat theme (no default browser
 * markdown styling).
 */
const markdownComponents: Components = {
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#00C805] underline underline-offset-2 hover:text-[#00b004] break-words"
    >
      {children}
    </a>
  ),
  p: ({ children }) => <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="line-through opacity-70">{children}</del>,
  h1: ({ children }) => <h1 className="text-lg font-bold mt-3 mb-1.5 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-base font-bold mt-3 mb-1.5 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-bold mt-2.5 mb-1 first:mt-0">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold mt-2 mb-1 first:mt-0">{children}</h4>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-2 last:mb-0 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 last:mb-0 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-muted-foreground/40 pl-3 italic text-muted-foreground my-2">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="border-border my-3" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 rounded-lg border border-border">
      <table className="text-xs w-full border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-secondary">{children}</thead>,
  th: ({ children }) => <th className="border-b border-border px-2 py-1.5 text-left font-semibold">{children}</th>,
  td: ({ children }) => <td className="border-b border-border/60 px-2 py-1.5">{children}</td>,
  input: ({ checked }) => <input type="checkbox" checked={!!checked} disabled className="mr-1.5 accent-[#00C805]" />,
  // Fenced blocks render via `pre` -> `code`. We build the whole styled block
  // inside `code` (language label, copy button, monospace pre) and make
  // `pre` a pass-through so we don't end up with a nested/duplicate <pre>.
  pre: ({ children }) => <>{children}</>,
  code(props) {
    const { className, children } = props;
    const codeText = String(children).replace(/\n$/, '');
    const languageMatch = /language-(\w+)/.exec(className || '');
    const isBlock = Boolean(languageMatch) || codeText.includes('\n');

    if (!isBlock) {
      return (
        <code className="rounded bg-secondary px-1.5 py-0.5 text-[0.85em] font-mono text-foreground break-words">
          {children}
        </code>
      );
    }

    return (
      <div className="rounded-xl overflow-hidden border border-border my-3 max-w-full">
        <div className="flex justify-between items-center px-3 py-2 bg-zinc-900 text-gray-300 text-xs">
          <span>{languageMatch?.[1] || 'text'}</span>
          <CopyButton text={codeText} />
        </div>
        <pre className="overflow-x-auto bg-zinc-950 text-gray-100 p-4 text-sm !my-0">
          <code className="font-mono whitespace-pre">{codeText}</code>
        </pre>
      </div>
    );
  },
};

/**
 * Renders a markdown text segment (a slice of a message between/around
 * `[[GENERATING:...]]` markers) with full GFM markdown support.
 */
function renderMarkdown(text: string, key: string): React.ReactNode {
  if (!text.trim()) return null;
  return (
    <ReactMarkdown key={key} remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {text}
    </ReactMarkdown>
  );
}

/**
 * Renders an assistant/agent message. The `[[GENERATING:...]]` marker the
 * backend emits for async image/video generation is sliced out and swapped
 * for its own polling widget. Everything else is rendered as full markdown
 * (headers, bold/italic, lists, links, blockquotes, inline/fenced code,
 * tables, etc.) via react-markdown + remark-gfm.
 */
function renderAgentContent(content: string): React.ReactNode {
  const segments: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  GENERATING_MARKER_REGEX.lastIndex = 0;
  while ((match = GENERATING_MARKER_REGEX.exec(content)) !== null) {
    const before = content.slice(lastIndex, match.index);
    const rendered = renderMarkdown(before, `seg-${key++}`);
    if (rendered) segments.push(rendered);
    segments.push(
      <GeneratingMediaBubble key={`gen-${key++}`} kind={match[1] as 'image' | 'video'} requestId={match[2]} />
    );
    lastIndex = GENERATING_MARKER_REGEX.lastIndex;
  }

  const remaining = content.slice(lastIndex);
  const rendered = renderMarkdown(remaining, `seg-${key++}`);
  if (rendered) segments.push(rendered);

  if (segments.length === 0) return null;

  return <div className="agent-markdown text-sm">{segments}</div>;
}

export const ChatPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();

  const { data: agent, loading: agentLoading } = useRealtimeData<AgentsResponse | null>(
    subscribeAgents,
    !!agentId,
    agentId ?? ''
  );

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const functional = !!agent && (isFunctionalAgentId(agentId) || agent.isFunctional);
  const helperText = getAgentHelperText(agentId);

  // Active fixed-window rental for this agent+renter, if any. While active,
  // per-call charging is skipped entirely (unlimited chat until expiry).
  // Live-updating so a mid-session expiry gracefully falls back to per-call
  // charging on the very next send.
  const { rental: activeRental, secondsRemaining } = useActiveAgentRental(agentId, user?.address);

  useEffect(() => {
    if (!agentId || !user?.address) return;
    const fromQuery = searchParams.get('session');
    setSessionId(fromQuery || getOrCreateSessionId(agentId, user.address));
  }, [agentId, user?.address, searchParams]);

  const filter = sessionId ? `sessionId='${sessionId}' limit 200` : '';
  const { data: messages } = useRealtimeData<ChatMessagesResponse[]>(
    subscribeManyChatMessages,
    !!sessionId,
    filter
  );

  const sortedMessages = (messages ?? []).slice().sort((a, b) => a.createdAt - b.createdAt);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [sortedMessages.length, sending]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const kind = detectAttachmentKind(file);
    if (!kind) {
      toast.error('Unsupported file. Please upload audio (.mp3/.wav/.m4a), an image (.png/.jpg/.gif/.webp), or a text/markdown document.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    const limit = maxBytesFor(kind);
    if (file.size > limit) {
      toast.error(`That ${kind} file is too large (max ${(limit / 1_000_000).toFixed(1)}MB).`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setAttachedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    const file = attachedFile;
    if ((!text && !file) || !agentId || !sessionId || !user?.address || !agent || sending) return;

    setInputValue('');
    setAttachedFile(null);
    setSending(true);
    try {
      // 1. Write the user's message immediately so it shows up in the thread.
      const displayParts = [file ? `📎 ${file.name}` : null, text || null].filter(Boolean) as string[];
      await setChatMessages(crypto.randomUUID(), {
        sessionId,
        agentId,
        renterWallet: Address.publicKey(user.address),
        sender: 'user',
        content: displayParts.join('\n'),
        createdAt: Time.Now,
      });

      // 2. Pay for THIS call (onchain SOL split) before running the agent —
      // unless the renter has an active rented window right now (re-checked
      // here, not just on mount, so a mid-session expiry falls back to
      // per-call charging on the very next send instead of the last one).
      if (!activeRental) {
        const payment = await payForCall(agent, user.address, sessionId);
        if (!payment.success) {
          toast.error(payment.error || 'Payment failed. Please try again.');
          return;
        }
      }

      // 3. Build the attachment payload (base64 for audio/image, plain text for documents).
      let attachment: PendingAttachmentPayload | undefined;
      if (file) {
        const kind = detectAttachmentKind(file);
        if (kind === 'document') {
          attachment = { kind, mimeType: file.type || 'text/plain', data: await fileToText(file), filename: file.name };
        } else if (kind) {
          attachment = { kind, mimeType: file.type, data: await fileToBase64(file), filename: file.name };
        }
      }

      // 4. Only now call the backend to run the agent.
      const token = await getIdToken();
      if (!token) {
        toast.error('Please reconnect your wallet and try again.');
        return;
      }
      const authApi = createAuthenticatedApiClient(token, user.address);
      await authApi.post('/api/agents/run', { agentId, sessionId, message: text, attachment });
    } catch (err) {
      console.error(err);
      const message = err instanceof Error && err.message ? err.message : 'The agent could not respond. Please try again.';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  if (!agentId) {
    return <ChatUnavailable />;
  }

  if (agentLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading agent…</div>
    );
  }

  if (!agent || !functional) {
    return <ChatUnavailable />;
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-muted-foreground">Connect your wallet to chat with this agent.</p>
        <button
          onClick={() => login()}
          className="rounded-full bg-[#00C805] text-white font-bold px-6 py-3 hover:bg-[#00b004] transition-colors"
        >
          Connect wallet
        </button>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        backgroundColor: 'hsl(var(--secondary))',
        backgroundImage: 'radial-gradient(circle, hsl(var(--border)) 1px, transparent 1.6px)',
        backgroundSize: '22px 22px',
      }}
    >
      {/* Header */}
      <div className="sticky top-0 z-20 flex items-center gap-3 bg-background/90 backdrop-blur border-b border-border py-3 px-4">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-secondary transition-colors" aria-label="Back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <AgentAvatar avatarUrl={agent.avatarUrl} avatarGradient={agent.avatarGradient} name={agent.name} size="sm" />
        <div className="flex-1 min-w-0">
          <p className="font-bold leading-tight truncate">{agent.name}</p>
          <p className="text-xs font-semibold flex items-center gap-1.5 text-[#00C805]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00C805] animate-pulse" />
            Online
          </p>
        </div>
        <button onClick={() => navigate('/')} className="p-2 rounded-full hover:bg-secondary transition-colors" aria-label="Home">
          <Home className="w-4 h-4 text-muted-foreground" />
        </button>
        <button className="p-2 rounded-full hover:bg-secondary transition-colors" aria-label="Info">
          <Info className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Active rental banner — unlimited chat until it expires */}
      {activeRental && (
        <div className="sticky top-[57px] z-10 flex items-center justify-center gap-2 bg-[#00C805]/12 border-b border-[#00C805]/25 py-2 px-4 text-xs font-semibold text-[#00b004]">
          <Clock className="w-3.5 h-3.5 shrink-0" />
          <span>Rented — unlimited access · {formatDuration(secondsRemaining)} left</span>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {sortedMessages.map((msg) => (
          <MessageBubble key={msg.id} msg={msg} agent={agent} />
        ))}

        {sending && (
          <div className="flex items-center gap-2 max-w-[80%]">
            <AgentAvatar avatarUrl={agent.avatarUrl} avatarGradient={agent.avatarGradient} name={agent.name} size="sm" />
            <div className="rounded-2xl bg-white shadow-sm px-4 py-3 flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:-0.3s]" />
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:-0.15s]" />
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" />
            </div>
          </div>
        )}

        {sortedMessages.length === 0 && !sending && (
          <p className="text-center text-sm text-muted-foreground py-10">
            Say hello to get started, or attach an image or document file.
          </p>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-border bg-background/90 backdrop-blur px-4 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
        {helperText && <p className="text-[11px] text-muted-foreground mb-2">{helperText}</p>}

        {attachedFile && (
          <div className="flex items-center gap-2 mb-2 bg-secondary rounded-full px-3 py-1.5 w-fit max-w-full">
            <Paperclip className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-medium truncate">{attachedFile.name}</span>
            <button
              onClick={() => setAttachedFile(null)}
              aria-label="Remove attachment"
              className="p-0.5 rounded-full hover:bg-accent transition-colors shrink-0"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,.png,.jpg,.jpeg,.gif,.webp,text/plain,text/markdown,.txt,.md,audio/mpeg,audio/mp3,audio/wav,audio/mp4,.mp3,.wav,.m4a"
            className="hidden"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            className="p-3 rounded-full bg-secondary hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 shrink-0"
            aria-label="Attach a file"
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type your message..."
            disabled={sending}
            className="flex-1 rounded-full bg-white shadow-sm border border-border px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-60"
          />
          <button
            onClick={handleSend}
            disabled={(!inputValue.trim() && !attachedFile) || sending}
            className="p-3 rounded-full bg-[#00C805] text-white hover:bg-[#00b004] transition-colors disabled:opacity-40 shrink-0"
            aria-label="Send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

const ChatUnavailable: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-muted-foreground">This agent isn't available for chat yet.</p>
      <button onClick={() => navigate(-1)} className="text-primary font-semibold">
        Go back
      </button>
    </div>
  );
};

const MessageBubble: React.FC<{ msg: ChatMessagesResponse; agent: AgentsResponse | null }> = ({ msg, agent }) => {
  if (msg.sender === 'system') {
    return (
      <div className="flex justify-center">
        <span className="text-xs font-semibold bg-white shadow-sm text-muted-foreground px-3 py-1.5 rounded-full border border-border">
          {msg.content}
        </span>
      </div>
    );
  }

  const isUser = msg.sender === 'user';

  return (
    <div className={`flex items-end gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
      {!isUser && (
        <AgentAvatar avatarUrl={agent?.avatarUrl} avatarGradient={agent?.avatarGradient} name={agent?.name ?? '?'} size="sm" />
      )}
      <div
        className={`relative max-w-[78%] rounded-2xl px-4 py-2.5 text-sm ${
          isUser ? 'bg-[#00C805] text-white rounded-br-md shadow-sm' : 'bg-white text-foreground rounded-bl-md shadow-sm'
        }`}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{msg.content}</p>
        ) : (
          <>
            {renderAgentContent(msg.content)}
            <MessageCopyButton text={msg.content} />
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Copy-only affordance for the entire agent/assistant reply (raw markdown
 * text). Separate from the per-code-block CopyButton above. Sits subtly in
 * the message bubble's corner — no edit capability, copy only.
 */
const MessageCopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy message');
    }
  };

  return (
    <button
      onClick={copy}
      aria-label="Copy message"
      title="Copy message"
      className="absolute -bottom-2 -right-2 p-1.5 rounded-full bg-white border border-border shadow-sm text-muted-foreground opacity-70 hover:opacity-100 hover:text-foreground transition-opacity"
    >
      {copied ? <Check className="w-3 h-3 text-[#00C805]" /> : <Copy className="w-3 h-3" />}
    </button>
  );
};

export default ChatPage;
